<script>
  import Button from '../Button/Button.svelte';
  import getTranslation from '../../utilities/getTranslation';
  import Select from '../Select/Select.svelte';
  import Icon from '../Icon/Icon.svelte';
  import { shouldShowOffer, isCustomerEligibleForOffer } from './helper';

  export let subscription, card, componentInformationKeys, showDarkBackground;

  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
  }
  const productMetadataIds = subscription.products.map((product) => product.metadataId);
  card.id = subscription.id;

  const currentUserSubscriptionId = subscription.products
    .filter(
      (product) =>
        product.metadataId === subscription.currentActiveSubscription ||
        product.aka.includes(subscription.currentActiveSubscription)
    )
    .map((product) => product.metadataId)[0];

  const hasActiveSubscription = !!subscription.currentActiveSubscription;

  const isMostPopular = productMetadataIds.find(
    (metadataId) =>
      metadataId === subscription.featuredProductMetadataId &&
      !hasActiveSubscription &&
      !showDarkBackground
  );

  const showOffer = shouldShowOffer(
    [subscription],
    hasActiveSubscription,
    subscription.isCustomerInactive
  );

  const offerInfo = {
    duration: getTranslation(subscription.informationKeys, 'pricing.offer.duration', ''),
    description: getTranslation(subscription.informationKeys, 'pricing.offer.description', ''),
  };

  const wrapperTitle = () => {
    if (isMostPopular) {
      return getTranslation(componentInformationKeys, 'subscription.mostpopular.v2');
    } else if (currentUserSubscriptionId) {
      return getTranslation(componentInformationKeys, 'subscription.currentsubscription.v2');
    } else {
      return '';
    }
  };

  const getLocalizationValue = (usps) =>
    usps.map((usp) => subscription.informationKeys[usp]).filter(Boolean);

  const allUsp = getLocalizationValue([
    'payment.usp.one.text.v2',
    'payment.usp.two.text.v2',
    'payment.usp.three.text.v2',
    'payment.usp.four.text.v2',
  ]);

  const options = subscription.products.map((product) => {
    const standardPrice = getTranslation(product.informationKeys, 'pricing.title.v2');
    return {
      id: product.metadataId,
      value: showOffer
        ? getTranslation(product.informationKeys, 'pricing.offer.title', standardPrice)
        : standardPrice,
    };
  });

  let selectedMetadataID = productMetadataIds[0];

  const getButtonText = (selectedMetadataID) => {
    const selectedProduct = subscription.products.find(
      (product) => product.metadataId === selectedMetadataID
    );

    if (subscription.currentActiveSubscription) {
      return getTranslation(selectedProduct.informationKeys, 'button.switch.start.v2');
    }

    if (subscription.isCustomerInactive || selectedProduct.trialDays === 0) {
      if (selectedProduct.recurring) {
        return getTranslation(selectedProduct.informationKeys, 'button.recurring.start.v2');
      }
      return getTranslation(selectedProduct.informationKeys, 'button.fixed.start.v2');
    }
    return getTranslation(selectedProduct.informationKeys, 'button.trial.start.v2');
  };

  const host =
    typeof window !== 'undefined' &&
    (window.location.host.indexOf('storytel.com') > -1 ||
      window.location.host.indexOf('storytel.dev') > -1 ||
      window.location.host.indexOf('mofibo.com') > -1)
      ? ''
      : subscription.countryIso === 'dk'
      ? 'https://mofibo.dk'
      : 'https://www.storytel.com';

  const getButtonUrl = (selectedMetadataID) => {
    const url = `${host}/${subscription.countryIso}/${subscription.language}/signup/${selectedMetadataID}`;

    const offerCode = subscription.products
      .filter((product) => product.metadataId === selectedMetadataID)
      .map((product) => product.informationKeys['pricing.offer.code'])
      .find((product) => product);

    if (
      isCustomerEligibleForOffer(hasActiveSubscription, subscription.isCustomerInactive) &&
      offerCode
    ) {
      return `${url}?campaignCode=${offerCode}`;
    }
    return url;
  };

  const gaCtaTracking = (selectedMetadataID) => {
    window.dataLayer.push({
      event: 'genericTrackingEvent',
      eventCategory: 'Subscription Selection Component',
      eventAction: 'Click Sign Up button',
      eventLabel: selectedMetadataID,
    });
  };

  const gaSelectTracking = (productGroupName) => {
    window.dataLayer.push({
      event: 'genericTrackingEvent',
      eventCategory: 'Subscription Selection Component',
      eventAction: 'Click on dropdown',
      eventLabel: productGroupName,
    });
  };

  const gaClickedOptionTracking = (selectedMetadataID, productGroupName) => {
    window.dataLayer.push({
      event: 'genericTrackingEvent',
      eventCategory: 'Subscription Selection Component',
      eventAction: 'Select product in dropdown',
      eventLabel: `${productGroupName} dropdown : ${selectedMetadataID} product`,
    });
  };

  const handleDropdownChange = (e) => {
    selectedMetadataID = parseInt(e.target.value);
    gaClickedOptionTracking(selectedMetadataID, subscription.name);
  };

  const getGroupPrice = (subscription) => {
    const standardPrice = getTranslation(subscription.informationKeys, 'description.price.v2');
    if (showOffer) {
      return getTranslation(subscription.informationKeys, 'pricing.offer.title', standardPrice);
    }

    return standardPrice;
  };

  const getProductPrice = (product) => {
    const standardPrice = getTranslation(product.informationKeys, 'pricing.title.v2');
    if (showOffer) {
      return getTranslation(product.informationKeys, 'pricing.offer.title', standardPrice);
    }
    return standardPrice;
  };
</script>

<style lang="scss">
  @import '../../styles/tools/media-query';
  @import '../../styles/settings/core';
  @import '../../styles/settings/global';
  @import '../../styles/settings/colors';

  $wrapper-blue: #036da4;

  .most-popular {
    display: flex;
    justify-content: center;
    height: 3.2rem;
    max-width: 34.1rem;
    background-color: $purple-40;
    color: $pink-10;
    padding-block-end: 0.5rem;
    padding: spacing(1) 0;
    border-radius: 1.3rem 1.3rem 0 0;
    box-shadow: 0 -4px 6px 0 rgba(0, 0, 0, 0.07);
    font-size: 1.3rem;
    font-weight: 600;
  }

  .has-current-plan {
    display: flex;
    height: 3.2rem;
    align-items: center;
    justify-content: center;
    border-radius: 1rem 1rem 0 0;
    background-color: $wrapper-blue;
    color: $white;
    box-shadow: 0 -4px 6px 0 rgba(0, 0, 0, 0.07);
    font-size: 1.3rem;
    font-weight: 600;

    &-dark {
      margin-block-start: -3.2rem;
      @include mq($from: desktop) {
        margin-block-start: -6.4rem;
      }
    }
  }

  .highlight-banner {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    top: 0;
    left: 0;
    max-width: 34.1rem;
    min-height: spacing(4);
    padding: 1rem 1.6rem;
    border-radius: 1rem 1rem 0 0;
    box-shadow: 0 -4px 6px 0 rgba(0, 0, 0, 0.05);
    background-color: $bright-green-30;
    color: rgb(46, 87, 46);
    font-size: 1.3rem;
    font-weight: 600;

    &.without-highlight-banner {
      background-color: $white;
      box-shadow: 0 -4px 6px 0 rgba(0, 0, 0, 0.05);
    }

    &.without-highlight-banner-dark {
      background-color: $white;
      @include mq($until: desktop) {
        margin-block-start: -0.2rem;
      }
    }

    &-and-most-popular {
      border-radius: 0;
      border-inline-start: $purple-40 spacing(0.5) solid;
      border-inline-end: $purple-40 spacing(0.5) solid;
      box-shadow: none;
    }

    &-with-current-plan {
      border-radius: 0;
      border-inline-start: $wrapper-blue spacing(0.5) solid;
      border-inline-end: $wrapper-blue spacing(0.5) solid;
    }

    &-dark {
      margin-block-start: -3.2rem;
      background-color: $bright-green-30;
    }
  }

  .card {
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
    max-width: 34.1rem;
    padding: 0 16px;
    background-color: $white;
    box-shadow: 0 5px 6px 0 rgba(0, 0, 0, 0.1);
    color: $purple-80;

    &.current-plan-border {
      border-radius: 0;
      border-inline-start: $wrapper-blue spacing(0.5) solid;
      border-inline-end: $wrapper-blue spacing(0.5) solid;
    }

    &.current-plan-card-wrapper {
      border-inline-start: $wrapper-blue spacing(0.5) solid;
      border-inline-end: $wrapper-blue spacing(0.5) solid;
      border-block-end: $wrapper-blue spacing(0.5) solid;
      border-radius: 0 0 spacing(1) spacing(1);
    }

    &.most-popular-border {
      border-inline-start: $purple-40 spacing(0.5) solid;
      border-inline-end: $purple-40 spacing(0.5) solid;
    }

    &.most-popular-card-wrapper {
      border-inline-start: $purple-40 spacing(0.5) solid;
      border-inline-end: $purple-40 spacing(0.5) solid;
      border-block-end: $purple-40 spacing(0.5) solid;
      border-radius: 0 0 spacing(1) spacing(1);
    }
  }

  .header {
    padding: spacing(2);
    font-size: 2.8rem;
    line-height: 3.2rem;
    letter-spacing: -0.0112em;
    margin: 0;

    @include mq($until: desktop) {
      margin-block-start: -0.2rem;
    }

    @include mq($from: tabletportrait) {
      font-size: 3.2rem;
      line-height: 4rem;
    }

    &.without-border-radius {
      border-radius: 0;
    }
  }

  .button-select {
    display: flex;
    justify-content: end;
    padding-block-end: spacing(4);
    border-radius: 0 0 10px 10px;

    @include mq($until: desktop) {
      margin-block-start: -0.3rem;
      margin-block-end: spacing(4);
    }

    &-dark {
      height: auto;
      @include mq($until: desktop) {
        margin-block-end: 6.4rem;
        margin-block-start: -0.5rem;
      }
    }

    &-multiple-cards-dark {
      @include mq($until: desktop) {
        margin-block-end: 6.4rem;
      }
    }
  }

  .product-description {
    margin: 0;

    p {
      margin-block-end: 0;

      @include mq($until: desktop) {
        z-index: 1;
      }
    }

    @include mq($until: desktop) {
      margin-block-start: -0.4rem;
    }
  }

  .subscription-start-button {
    margin-block-start: spacing(4);
  }

  .label {
    margin-block-end: spacing(1);
    font-size: 1.3rem;
    color: $purple-50;
  }

  .subscription-description-list {
    list-style-type: none;
    padding-block-end: spacing(2);
    color: $purple-80;
    fill: $purple-80;

    @include mq($until: desktop) {
      margin-block-start: -0.8rem;
    }

    :global(svg) {
      margin-block-start: spacing(0.5);
    }

    .list-item {
      display: grid;
      grid-template-columns: spacing(3) auto;
      margin-block-end: spacing(2);
    }
  }

  .price-label {
    color: $purple-50;
    padding: spacing(4) spacing(2);
    margin: 0;
    font-weight: 600;

    p {
      margin-block-end: 0;

      &.price-without-offer {
        margin-block-start: 1.4rem;
      }

      :global(del) {
        color: $purple-30;
      }

      :global(span) {
        font-size: 2.8rem;
      }
    }

    @include mq($until: desktop) {
      margin-block-start: -0.6rem;
    }

    .badge {
      width: fit-content;
      height: 3.2rem;
      font-size: 1.3rem;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: $bright-green-30;
      color: $green-50;
      border-radius: 1rem;
      padding-inline: 1.6rem;
      margin-block-start: 1.6rem;

      > p {
        margin-block-end: 0;
      }
    }
  }
</style>

<div
  class:most-popular={isMostPopular}
  class:has-current-plan={currentUserSubscriptionId}
  class:has-current-plan-dark={currentUserSubscriptionId && showDarkBackground}
  bind:this={card.wrapper}
>
  {wrapperTitle()}
</div>
<div
  class="highlight-banner"
  class:without-highlight-banner={!showOffer || !offerInfo.duration}
  class:without-highlight-banner-dark={showDarkBackground && currentUserSubscriptionId}
  class:highlight-banner-and-most-popular={isMostPopular}
  class:highlight-banner-with-current-plan={currentUserSubscriptionId}
  class:highlight-banner-dark={showDarkBackground}
  id={subscription.id}
  bind:this={card.banner}
>
  {#if showOffer && offerInfo.duration}
    <span>{offerInfo.duration}</span>
  {/if}
</div>
<h3
  class="card header"
  class:most-popular-border={isMostPopular}
  class:current-plan-border={currentUserSubscriptionId}
  class:without-border-radius={subscription.highlightBannerLabel}
  bind:this={card.header}
>
  {getTranslation(subscription.informationKeys, 'description.title.v2')}
</h3>
<div
  class="card product-description"
  class:most-popular-border={isMostPopular}
  class:current-plan-border={currentUserSubscriptionId}
  bind:this={card.description}
>
  <p>
    {@html getTranslation(subscription.informationKeys, 'description.body.v2')}
  </p>
</div>
<div
  class="card price-label"
  class:most-popular-border={isMostPopular}
  class:current-plan-border={currentUserSubscriptionId}
  bind:this={card.price}
>
  <p class:price-without-offer={!showOffer || !offerInfo.duration}>
    {#if subscription.products.length > 1}
      {@html getGroupPrice(subscription)}
    {:else}
      {#each subscription.products as product}
        {@html getProductPrice(product)}
      {/each}
    {/if}
  </p>
  {#if showOffer && offerInfo.description}
    <div class="badge">
      <p>
        {offerInfo.description.toUpperCase()}
      </p>
    </div>
  {/if}
</div>
<ul
  class="card subscription-description-list"
  class:most-popular-border={isMostPopular}
  class:current-plan-border={currentUserSubscriptionId}
  bind:this={card.descriptionList}
>
  {#each allUsp as usp}
    <li class="list-item">
      <Icon iconName="checkmark" size="16" />
      {usp}
    </li>
  {/each}
</ul>
<div
  class="card button-select"
  class:most-popular-card-wrapper={isMostPopular}
  class:current-plan-card-wrapper={currentUserSubscriptionId}
  class:current-plan-select={currentUserSubscriptionId}
  class:button-select-dark={showDarkBackground}
  class:button-select-multiple-cards-dark={showDarkBackground &&
    subscription.products.length >= 2 &&
    currentUserSubscriptionId}
  bind:this={card.button}
>
  {#if subscription.products.length > 1}
    <label for={subscription.id} class="label"
      >{getTranslation(subscription.informationKeys, 'pricing.title.v2')}</label
    >
    <Select
      id={subscription.id}
      {options}
      borderColor="purple"
      selected={selectedMetadataID}
      on:change={(e) => handleDropdownChange(e)}
      on:click={gaSelectTracking(subscription.name)}
    />
  {/if}
  <div />
  <div class:subscription-start-button={subscription.products.length > 1}>
    <Button
      fullwidth={true}
      link={getButtonUrl(selectedMetadataID)}
      disabled={currentUserSubscriptionId === selectedMetadataID}
      on:click={gaCtaTracking(selectedMetadataID)}
    >
      {getButtonText(selectedMetadataID)}
    </Button>
  </div>
</div>
